import { createContext, useCallback, useContext, useEffect, useState, ReactNode, useMemo, useRef } from 'react';
import { FormikContextType, FormikProvider, FormikTouched, FormikValues, useFormik } from 'formik';
import * as Yup from 'yup';
import { cloneDeepWith, get } from 'lodash';
import { StepConfig } from './types';

type ResetEvent<T> = {
    stepIndex: number;
    stepKey: keyof T;
    fieldKey: string;
};

type MultiStepFormProviderProps<T> = {
    steps: StepConfig<T>[];
    initialValues: T;
    autocompleteDelayMs?: number;
    onInitialize?: (formik: FormikContextType<T>) => void;
    onSubmit?: (values: T) => Promise<void>;
    children: ReactNode;
};

type MultiStepFormContextType<T> = {
    currentStep: number;
    stepsConfig: StepConfig<T>[];
    currentStepConfig?: StepConfig<T>;
    isActive: (stepKey: keyof T) => boolean;
    isIdle: (stepKey: keyof T) => boolean;
    isComplete: (stepKey: keyof T) => boolean;
    isReadyToSubmit: () => boolean;
    resetStep: (stepKey: keyof T, fieldKey: string) => void;
    goForward: (targetStepIndex: number) => Promise<void>;
    goNext: () => Promise<void>;
    isSubmitting: boolean;
    lastResetEvent?: ResetEvent<T> | null;
    handleSubmit: (e: React.FormEvent<HTMLFormElement> | undefined) => void;
};

const MultiStepFormContext = createContext<MultiStepFormContextType<any> | null>(null);

export const useMultiStepFormContext = <T,>() => {
    const context = useContext(MultiStepFormContext);
    if (!context) throw new Error('useMultiStepFormContext must be used within MultiStepFormProvider');
    return context as MultiStepFormContextType<T>;
};

export function MultiStepFormProvider<T extends FormikValues>({
    steps,
    initialValues,
    autocompleteDelayMs = 1200,
    onInitialize,
    onSubmit,
    children,
}: MultiStepFormProviderProps<T>) {
    const [currentStep, setCurrentStep] = useState(0);
    const [isResetting, setIsResetting] = useState(false);
    const [autoProgressTimeout, setAutoProgressTimeout] = useState<NodeJS.Timeout | null>(null);
    const [lastResetEvent, setLastResetEvent] = useState<ResetEvent<T> | null>(null);

    const currentStepConfig = steps[currentStep] ?? {};
    const baseInitialValuesRef = useRef(initialValues);

    const currentKey = currentStepConfig?.key;

    const currentSchema = useMemo(() => {
        return currentStepConfig?.disabled ? Yup.object() : currentStepConfig?.validationSchema;
    }, [currentStepConfig?.disabled, currentStepConfig?.validationSchema]);

    const formik = useFormik<T>({
        initialValues,
        validateOnChange: false,
        validateOnBlur: true,
        validationSchema: currentStepConfig?.disabled
            ? undefined
            : Yup.object({ [currentKey]: currentStepConfig?.validationSchema }),
        onSubmit: async (values) => {
            if (onSubmit) await onSubmit(values);
        },
    });

    const deepTrue = (value: unknown): any =>
        cloneDeepWith(value, (val) => {
            if (Array.isArray(val) || (val !== null && typeof val === 'object')) return undefined;
            return true;
        });

    function makeStepTouched<T>(values: T, stepKey: keyof T): FormikTouched<T> {
        const stepValues = get(values as any, stepKey as any);
        return { [stepKey]: deepTrue(stepValues) } as unknown as FormikTouched<T>;
    }

    const validateCurrentStep = async () => {
        try {
            await Yup.object({ [currentKey]: currentStepConfig.validationSchema }).validate(formik.values, {
                abortEarly: false,
            });
            return true;
        } catch {
            return false;
        }
    };

    const getNextEnabledStep = (currentIndex: number, config: StepConfig<T>[]): number => {
        let next = currentIndex + 1;
        while (next < config.length && config[next].disabled) next++;
        return next;
    };

    const getNextRequiredStep = (currentIndex: number, config: StepConfig<T>[]): number | null => {
        for (let i = currentIndex + 1; i < config.length; i++) {
            const s = config[i];
            if (!s.disabled && !s.optional) return i;
        }
        return null;
    };

    const getLastEnabledStepBetween = <T,>(from: number, config: StepConfig<T>[], upper: number): number => {
        const last = Math.min(upper, config.length - 1);
        let idx = -1;
        for (let i = from + 1; i <= last; i++) {
            if (!config[i]?.disabled) idx = i;
        }
        return idx;
    };

    // Progressing forward manually
    // Allows skipping optional/disabled steps
    // If there is a required step between the current step and the target step
    // set the current step to the first required step
    const goForward = useCallback(
        async (targetStepIndex: number) => {
            if (isResetting || targetStepIndex <= currentStep) return;

            // Clear any pending autoprogression
            if (autoProgressTimeout) clearTimeout(autoProgressTimeout);

            // Validate current step
            const canAdvance = currentStepConfig?.disabled ? true : await validateCurrentStep();

            if (!canAdvance) {
                // Show the errors for the current step
                await formik.setTouched(
                    makeStepTouched(formik.values, currentStepConfig.key as keyof typeof formik.values),
                    true
                );
                return;
            }

            const upperBound = Math.min(targetStepIndex, steps.length - 1);

            let nextIndex = getNextRequiredStep(currentStep, steps);

            if (nextIndex === null) {
                nextIndex = getLastEnabledStepBetween(currentStep, steps, upperBound);
                if (nextIndex === -1) return;
            }

            if (nextIndex <= currentStep) return;
            setCurrentStep(nextIndex);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [autoProgressTimeout, currentStep, currentStepConfig?.disabled, formik, isResetting, steps, validateCurrentStep]
    );

    const goNext = useCallback(async () => {
        const nextEnabled = getNextEnabledStep(currentStep, steps);
        if (nextEnabled < steps.length) {
            await goForward(nextEnabled);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep, steps, goForward]);

    const handleAutoProgression = useCallback(async () => {
        if (currentStepConfig.skipAutoProgress) {
            return;
        }

        if (!currentKey || !currentSchema || isResetting) return;

        if (autoProgressTimeout) {
            clearTimeout(autoProgressTimeout);
        }

        const timeoutId = setTimeout(async () => {
            try {
                // Skip validation for disabled steps
                if (currentStepConfig?.disabled) {
                    const nextStepIndex = getNextEnabledStep(currentStep, steps);
                    setCurrentStep(nextStepIndex);
                    return;
                }

                const isValid = await validateCurrentStep();

                if (!isValid) {
                    return;
                }

                const nextStepIndex = getNextEnabledStep(currentStep, steps);
                setCurrentStep(nextStepIndex);
            } catch (error) {
                console.debug('Auto-progression validation failed:', error);
            }
        }, autocompleteDelayMs);

        setAutoProgressTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoProgressTimeout, currentKey, currentSchema, currentStep, currentStepConfig?.disabled, validateCurrentStep]);

    const resetStep = (stepKey: string | number | symbol, fieldKey: string) => {
        const stepIndex = steps.findIndex((s) => s.key === stepKey);
        if (stepIndex === -1) return;

        const preservedValues: Partial<T> = {};
        steps.forEach((s, i) => {
            if (i < stepIndex) preservedValues[s.key] = formik.values[s.key];
        });

        const stepValues = { ...formik.values[stepKey as keyof T], [fieldKey]: '' };
        const newValues = {
            ...baseInitialValuesRef.current,
            ...preservedValues,
            [stepKey]: stepValues,
        };

        setIsResetting(true);
        formik.setTouched({});
        formik.setErrors({});
        formik.resetForm({ values: newValues });
        setCurrentStep(stepIndex);
        setLastResetEvent({
            stepIndex,
            stepKey: steps[stepIndex].key as keyof T,
            fieldKey,
        });

        setTimeout(() => setIsResetting(false), 100);
    };

    const isIdle = (stepKey: string | number | symbol) => steps.findIndex((s) => s.key === stepKey) > currentStep;
    const isActive = (stepKey: string | number | symbol) => steps.findIndex((s) => s.key === stepKey) === currentStep;
    const isComplete = (stepKey: string | number | symbol) => steps.findIndex((s) => s.key === stepKey) < currentStep;
    const isReadyToSubmit = () => {
        // Find the last required step (not disabled, not optional)
        let lastRequiredIndex = -1;
        for (let i = 0; i < steps.length; i++) {
            const s = steps[i];
            if (!s.disabled && !s.optional) lastRequiredIndex = i;
        }

        if (lastRequiredIndex === -1) return true;
        if (currentStep > lastRequiredIndex) return true;

        // A step that opts out of auto-progression never moves the wizard past itself,
        // so readiness comes from its own values instead of the step index.
        const lastRequiredStep = steps[lastRequiredIndex];
        if (currentStep === lastRequiredIndex && lastRequiredStep.skipAutoProgress) {
            return Yup.object({ [lastRequiredStep.key as string]: lastRequiredStep.validationSchema }).isValidSync(
                formik.values
            );
        }

        return false;
    };

    useEffect(() => {
        if (onInitialize) {
            onInitialize(formik);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        handleAutoProgression();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values]);

    // A required step that already holds a valid value (e.g. a default) completes on entry.
    // Optional steps are skipped: their empty values are valid, so they would always be skipped over.
    useEffect(() => {
        if (currentStepConfig.optional) return;
        handleAutoProgression();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep]);

    return (
        <MultiStepFormContext.Provider
            value={{
                currentStep,
                currentStepConfig,
                stepsConfig: steps,
                isActive,
                isIdle,
                isComplete,
                isReadyToSubmit,
                resetStep,
                goForward,
                goNext,
                isSubmitting: formik.isSubmitting,
                lastResetEvent,
                handleSubmit: formik.handleSubmit,
            }}
        >
            <FormikProvider value={formik}>{children}</FormikProvider>
        </MultiStepFormContext.Provider>
    );
}
